import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { parseErrorCode } from "@/lib/queryClient";
import {
  PADSPLIT_DIRECT_MESSAGE_URL,
  PADSPLIT_SEND_ERROR_TITLE,
  PADSPLIT_SEND_ERROR_BODY,
  PADSPLIT_SEND_FAILED_CODE,
} from "@shared/messaging-outage";

/**
 * True when an error thrown by a send mutation is a PadSplit delivery failure
 * (server returned `code: "PADSPLIT_SEND_FAILED"`). Callers use this to show the
 * dedicated pop-up below instead of the generic "couldn't send" toast.
 */
export function isPadsplitSendError(err: unknown): boolean {
  return parseErrorCode(err) === PADSPLIT_SEND_FAILED_CODE;
}

export function PadsplitSendErrorDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-padsplit-send-error">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-brand" />
            {PADSPLIT_SEND_ERROR_TITLE}
          </DialogTitle>
          <DialogDescription data-testid="text-padsplit-send-error-body">
            {PADSPLIT_SEND_ERROR_BODY}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-row sm:justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="button-padsplit-send-error-dismiss"
          >
            Close
          </Button>
          <Button asChild data-testid="link-padsplit-send-error-open">
            <a
              href={PADSPLIT_DIRECT_MESSAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onOpenChange(false)}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Send through PadSplit
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
